import { isDefined } from "~/shared/common";
import type { Option, OptionUpdate, Property } from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid } from "~/utils/common";
import { makeHierarchicalOptionComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSortedMap, setObjectMapAndSortedMapGrouped, updateInSortedMap } from "./utils";

export type State = {
  _duidsToOptions: Map<string, Option>;
  _sortedOptionDuidsByPropertyDuid: Map<string, string[]>;
};

export const state: State = {
  _duidsToOptions: new Map(),
  _sortedOptionDuidsByPropertyDuid: new Map(),
};

export type Getters = {
  getOptionList: (property: Property) => Option[];
  getOptionByDuid: (duid: string) => Option | undefined;
  getOptionsByDuidsOrdered: (duids: string[], property: Property) => Option[];
  getOptionByTitle: (title: string, property: Property, parentDuid?: string | null) => Option | undefined;
};

export type Actions = {
  createOption: (
    title: string,
    property: Property,
    partialOption?: Partial<Option>,
    options?: { awaitBackend?: boolean }
  ) => Promise<Option | undefined>;
  updateOption: (update: OptionUpdate, options?: { awaitBackend?: boolean }) => Promise<Option | undefined>;
  deleteOption: (option: Option, options?: { awaitBackend?: boolean }) => Promise<void>;
  $createOptionNoBackend: (title: string, property: Property, partialOption?: Partial<Option>) => Option | undefined;
  $setOptions: (options: Option[]) => void;
  $createOrUpdateOption: (option: Option) => void;
  $deleteOption: (option: Option) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getOptionList() {
    return (property) =>
      (this._sortedOptionDuidsByPropertyDuid.get(property.duid) ?? [])
        .map((e) => this._duidsToOptions.get(e))
        .filter(isDefined);
  },
  getOptionByDuid() {
    return (duid) => this._duidsToOptions.get(duid);
  },
  getOptionsByDuidsOrdered() {
    return (duids, property) => this.getOptionList(property).filter((e) => duids.includes(e.duid));
  },
  getOptionByTitle() {
    return (title, property, parentDuid) => {
      const normTitle = title.trim().toLowerCase();
      return this.getOptionList(property).find(
        (e: Option) => (parentDuid === undefined || e.parentDuid === parentDuid) && e.title.toLowerCase() === normTitle
      );
    };
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createOption(title, property, partialOption = {}, options = {}) {
    const { awaitBackend = false } = options;
    const option = this.$createOptionNoBackend(title, property, partialOption);
    if (!option) {
      return undefined;
    }

    const backendAction = this.$backend.option.create(option);
    if (awaitBackend) {
      await backendAction;
    }
    return option;
  },
  async updateOption(update, options = {}) {
    const { awaitBackend = false } = options;
    const option = this.getOptionByDuid(update.duid);
    if (!option) {
      return undefined;
    }

    Object.assign(option, update);
    updateInSortedMap(
      this._duidsToOptions,
      this._sortedOptionDuidsByPropertyDuid,
      option.propertyDuid,
      option.duid,
      makeHierarchicalOptionComparator
    );

    const backendAction = this.$backend.option.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return option;
  },
  async deleteOption(option, options = {}) {
    const { awaitBackend = false } = options;

    this.$cleanupFormFieldsDefaultsNoBackend([option.duid], [option.propertyDuid]);
    this.$cleanupLayoutsFiltersNoBackend([option.duid], [option.propertyDuid]);
    this.$cleanupTasksPropertyValuesNoBackend([option.duid], [option.propertyDuid]);

    this.$deleteOption(option);

    const backendAction = this.$backend.option.delete(option.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $createOptionNoBackend(title, property, partialOption = {}) {
    const { parentDuid } = partialOption;
    if (title !== "" && this.getOptionByTitle(title, property, parentDuid) !== undefined) {
      return undefined;
    }

    const option: Option = {
      duid: makeDuid(),
      propertyDuid: property.duid,
      parentDuid: null,
      title: title.trim(),
      colorHex: makeRandomColorHex(),
      ...partialOption,
    };

    this.$createOrUpdateOption(option);

    return option;
  },
  $setOptions(options) {
    setObjectMapAndSortedMapGrouped(
      this._duidsToOptions,
      this._sortedOptionDuidsByPropertyDuid,
      options,
      makeHierarchicalOptionComparator
    );
  },
  $createOrUpdateOption(option) {
    const currentOption = this.getOptionByDuid(option.duid);

    if (!currentOption) {
      this._duidsToOptions.set(option.duid, option);
    } else {
      Object.assign(currentOption, option);
    }
    updateInSortedMap(
      this._duidsToOptions,
      this._sortedOptionDuidsByPropertyDuid,
      option.propertyDuid,
      option.duid,
      makeHierarchicalOptionComparator
    );
  },
  $deleteOption(option) {
    this._duidsToOptions.delete(option.duid);
    removeFromSortedMap(this._sortedOptionDuidsByPropertyDuid, option.propertyDuid, option.duid);
  },
};

export { actions, getters };
